import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLinkedin, FaEnvelope, FaArrowRight } from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';
import logo from '../assets/Logo-Blue-white.webp';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ThemeToggle from './ThemeToggle/ThemeToggle.jsx';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher.jsx';

const Navbar = ({ onActiveTabChange }) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('HeroSection');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };

    // getBoundingClientRect() forces the browser to recompute layout
    // synchronously. Calling it on every raw scroll event (which can fire
    // dozens of times per frame) was showing up as a "forced reflow" in
    // Lighthouse. Coalescing the work into a single rAF callback per frame
    // fixes that without changing the active-section behavior.
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShowScrollTop(window.pageYOffset > 300);

        const sections = ['HeroSection', 'AboutSection', 'DesignServices', 'WhySection', 'ContactUs'];
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 100) {
              setActiveSection(section);
              break;
            }
          }
        }
        ticking = false;
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollTo = (id, isService) => {
    if (isService) {
      onActiveTabChange(id);
      const element = document.getElementById('all');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(id);
        setIsOpen(false);
      }
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(id);
        setIsOpen(false);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveSection('HeroSection');
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  // Very narrow phones: hide the icon row from the top bar (keep only the
  // hamburger) so logo + icons + hamburger don't crowd/overlap. Icons are
  // still reachable inside the opened mobile menu either way.
  const isExtraSmall = windowWidth < 400;

  // Main nav items — order matches the approved design (We Are / We Deliver
  // / Why Vizcorn / Build Together). Active state = bold white label with a
  // blue underline, everything else is muted gray.
  const navItems = [
    { id: 'AboutSection', label: t('nav.weAre') },
    { id: 'DesignServices', label: t('nav.weDeliver') },
    { id: 'WhySection', label: t('nav.whyVizcorn') },
    { id: 'ContactUs', label: t('nav.buildTogether') },
  ];

  const NavLink = ({ item, mobile }) => {
    const isActive = activeSection === item.id;
    return (
      <motion.button
        onClick={() => mobile ? (scrollTo(item.id), setIsOpen(false)) : scrollTo(item.id)}
        className={`relative font-semibold text-sm transition-colors duration-200 ${
          mobile
            ? `text-left px-4 py-3 rounded-lg ${isActive ? 'text-white' : 'text-nav-text hover:text-white hover:bg-white/5'}`
            : `px-1 py-2 ${isActive ? 'text-nav-text-active' : 'text-nav-text hover:text-white'}`
        }`}
        whileTap={{ scale: 0.96 }}
      >
        {item.label}
        {!mobile && (
          <span
            className={`absolute left-0 right-0 -bottom-1 h-[2px] rounded-full bg-vizcorn-blue transition-all duration-200 ${
              isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
          />
        )}
      </motion.button>
    );
  };

  return (
    <>
      {/* bg-black/80 (not /40): at black/40 this blends down to ~1.85:1
          contrast against nav-text when it sits over the light hero at
          the top of the page — fails WCAG AA. /80 keeps it >8:1 against
          any section background. */}
      <nav className="bg-black/80 backdrop-blur-lg border-b border-nav-border px-4 sm:px-6 py-3 fixed w-full top-0 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Logo */}
          <div className="w-auto flex-shrink-0">
            <Link to={`/${lang}`} className="flex items-center gap-2">
              <img
                src={logo}
                alt="Vizcorn"
                width={170}
                height={52}
                fetchPriority="high"
                className="w-[150px] sm:w-[170px] h-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              />
            </Link>
          </div>

          {/* Center Navigation */}
          {!isMobile && (
            <div className={`flex items-center ${isTablet ? 'gap-5' : 'gap-8'}`}>
              {navItems.map((item) => (
                <NavLink key={item.id} item={item} />
              ))}
            </div>
          )}

          {/* Right side: Social + Divider + CTA */}
          {!isMobile && (
            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href="mailto:contact@vizcorn.com"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Email"
              >
                <FaEnvelope className="text-sm" />
              </a>
              <a
                href="https://www.linkedin.com/company/vizcornkft/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white hover:bg-white/10 transition-colors"
                aria-label="LinkedIn"
              >
                <FaLinkedin className="text-sm" />
              </a>

              <LanguageSwitcher />
              <ThemeToggle />

              <span className="w-px h-6 bg-nav-border mx-1" />

              <motion.button
                onClick={() => scrollTo('ContactUs')}
                className="inline-flex items-center gap-2 bg-vizcorn-blue hover:bg-vizcorn-blue-dark text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {t('nav.letsTalk')}
                <FaArrowRight className="text-xs" />
              </motion.button>
            </div>
          )}

          {/* Mobile: icons (hidden on very narrow phones) + hamburger */}
          {isMobile && (
            <div className="flex items-center gap-2">
              {!isExtraSmall && (
                <>
                  {/* <a
                    href="mailto:contact@vizcorn.com"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white transition-colors"
                    aria-label="Email"
                  >
                    <FaEnvelope className="text-sm" />
                  </a> */}
                  {/* <a
                    href="https://www.linkedin.com/company/vizcornkft/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white transition-colors"
                    aria-label="LinkedIn"
                  >
                    <FaLinkedin className="text-sm" />
                  </a> */}
                  {/* <LanguageSwitcher /> */}
                  <ThemeToggle />
                </>
              )}
              <button
                onClick={toggleMenu}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {isOpen ? <HiX className="text-lg" /> : <HiMenu className="text-lg" />}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu — rendered OUTSIDE <nav> on purpose: <nav> has
          backdrop-blur-lg, and a backdrop-filter/filter on an ancestor
          creates a new containing block for position:fixed descendants,
          which broke this drawer's full-viewport fixed positioning when
          it lived inside <nav>. */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay that closes the menu when clicked */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black z-30 md:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Mobile menu */}
            <motion.div
              key="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              // bg-white/30 (previous) blended down to a near-white panel,
              // but the nav links use light-gray/white text meant for the
              // dark navbar — that combo was ~1.5:1 contrast (WCAG AA needs
              // 4.5:1), i.e. the mobile menu text was nearly invisible.
              // nav-bg/95 keeps the same dark theme as the top bar so the
              // existing text colors stay legible.
              className="fixed top-0 right-0 w-72 max-w-[85vw] h-full bg-nav-bg/95 backdrop-blur-xl border-l border-nav-border shadow-lg px-6 py-10 z-40 flex flex-col gap-4 md:hidden"
            >
              {/* Close button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 p-2 text-nav-text hover:text-white"
                aria-label="Close menu"
              >
                <HiX className="text-xl" />
              </button>

              {navItems.map((item) => (
                <NavLink key={`mobile-${item.id}`} item={item} mobile />
              ))}

              {/* Social + Theme toggle in Mobile */}
              <div className="flex items-center gap-3 pt-6 mt-6 border-t border-nav-border">
                <a
                  href="mailto:contact@vizcorn.com"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white transition-colors"
                  aria-label="Email"
                >
                  <FaEnvelope className="text-sm" />
                </a>
                <a
                  href="https://www.linkedin.com/company/vizcornkft/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-nav-surface text-nav-text hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="text-sm" />
                </a>
                <LanguageSwitcher />
                <ThemeToggle />
              </div>

              <button
                onClick={() => {
                  scrollTo('ContactUs');
                  setIsOpen(false);
                }}
                className="inline-flex items-center justify-center gap-2 bg-vizcorn-blue hover:bg-vizcorn-blue-dark text-white text-sm font-semibold px-4 py-3 rounded-lg transition-colors mt-2"
              >
                {t('nav.letsTalk')}
                <FaArrowRight className="text-xs" />
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
